import styles from "./user-management-page.module.scss";
import { getRolesDescriptions } from "@src/cloud-connect/roles-permissions/use-cases/get-roles-descriptions";
import { errorSnackbar } from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import { Spacer16 } from "@src/components/atoms/spacer-16/spacer-16";
import { GatewayServiceUsersListView } from "@src/components/organisms/gateway-service-users-list-view/gateway-service-users-list-view";
import { ServiceUsersListView } from "@src/components/organisms/service-users-list-view/service-users-list-view";
import { UserManagementListPillsNav } from "@src/components/organisms/user-management-list-pills-nav/user-management-list-pills-nav";
import { UsersListView } from "@src/components/organisms/users-list-view/users-list-view";
import { FlexRowContainer } from "@src/components/templates/layouts/flex-row-container/flex-row-container";

import { MainContainer } from "@src/components/templates/layouts/main-container/main-container";
import { RoundBorderComponentContainer } from "@src/components/templates/layouts/round-border-component-container/round-border-component-container";
import { useI18N } from "@src/language/language-context";
import { RootState } from "@src/reducers/store";
import { updateRolesDescriptions } from "@src/reducers/your-reducer";
import {
  UserManagementListActiveRoleEnum,
  UserManagementListActiveRoleRenderDataType,
  ZitadelGatewayServiceUser,
  ZitadelServiceUser,
  ZitadelUser,
} from "@src/types/user-management";
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

const cssClassName = "user-management-page";

export const UserManagementPage = () => {
  const dispatch = useDispatch();
  const i18n = useI18N();

  const userManagementListActiveView = useSelector(
    (state: RootState) => state.yourSlice.userManagementListActiveView,
  );

  const permissions = useSelector(
    (state: RootState) => state.yourSlice.authContext?.permissions,
  );

  const [
    userManagementListActiveRoleData,
    setUserManagementListActiveRoleData,
  ] = useState<UserManagementListActiveRoleRenderDataType>([]);

  const [
    userManagementListActiveRoleRenderData,
    setUserManagementListActiveRoleRenderData,
  ] = useState<UserManagementListActiveRoleRenderDataType>([]);

  const handleRenderDataChange = useCallback(
    (nextData: UserManagementListActiveRoleRenderDataType) => {
      setUserManagementListActiveRoleRenderData(nextData);
    },
    [],
  );

  const texts = {
    errorLoadingRolesText: i18n(
      "pages.userManagementPage.errorLoadingRolesText",
    ),
  };

  const loadRolesDescriptions = async () => {
    const result = await getRolesDescriptions();

    if (result.success && result.data) {
      dispatch(updateRolesDescriptions(result.data));
    } else {
      console.error("error loading roles descriptions:", result.error);
      errorSnackbar(texts.errorLoadingRolesText, 5000);
    }
  };

  useEffect(() => {
    loadRolesDescriptions();
  }, []);

  const renderActiveView = useCallback(() => {
    if (!permissions) {
      return;
    }

    switch (userManagementListActiveView) {
      case UserManagementListActiveRoleEnum.USERS:
        return (
          <UsersListView
            usersListData={
              userManagementListActiveRoleRenderData as ZitadelUser[]
            }
            userManagementListActiveRoleData={userManagementListActiveRoleData}
            onUsersLoaded={setUserManagementListActiveRoleData}
            onRenderDataChange={handleRenderDataChange}
          />
        );
      case UserManagementListActiveRoleEnum.SERVICE_USERS:
        return (
          <ServiceUsersListView
            serviceUsersListData={
              userManagementListActiveRoleRenderData as ZitadelServiceUser[]
            }
            userManagementListActiveRoleData={userManagementListActiveRoleData}
            onServiceUsersLoaded={setUserManagementListActiveRoleData}
            onRenderDataChange={handleRenderDataChange}
          />
        );
      case UserManagementListActiveRoleEnum.GATEWAY_SERVICE_USERS:
        return (
          <GatewayServiceUsersListView
            gatewayServiceUsersListData={
              userManagementListActiveRoleRenderData as ZitadelGatewayServiceUser[]
            }
            userManagementListActiveRoleData={userManagementListActiveRoleData}
            onGatewayServiceUsersLoaded={setUserManagementListActiveRoleData}
            onRenderDataChange={handleRenderDataChange}
          />
        );
      default:
        return (
          <UsersListView
            usersListData={
              userManagementListActiveRoleRenderData as ZitadelUser[]
            }
            userManagementListActiveRoleData={userManagementListActiveRoleData}
            onUsersLoaded={setUserManagementListActiveRoleData}
            onRenderDataChange={handleRenderDataChange}
          />
        );
    }
  }, [
    handleRenderDataChange,
    userManagementListActiveRoleData,
    userManagementListActiveView,
    userManagementListActiveRoleRenderData,
    permissions,
  ]);

  return (
    <MainContainer>
      <>
        <Spacer16 />
        <FlexRowContainer justifyContent="center">
          <UserManagementListPillsNav
            activeView={userManagementListActiveView}
          />
        </FlexRowContainer>

        <Spacer16 />
        <RoundBorderComponentContainer>
          <>
            <div className={styles[`${cssClassName}__list-wrapper`]}>
              {renderActiveView()}
            </div>
          </>
        </RoundBorderComponentContainer>
      </>
    </MainContainer>
  );
};
